import type { FC } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	HelpCircleIcon,
	ThumbsDownIcon,
	ThumbsUpIcon
} from '@/components/ui/icons'
import { Tooltip } from '@/components/ui/tooltip'
import type { VettingItemStatus } from '@/features/ops-vetting/types.ts'
import { useTranslation } from '@/translations/i18n'

interface VettingSectionStatusSelectorProps {
	value: VettingItemStatus | null
	onChange: (service: VettingItemStatus) => void
	isLoading?: boolean
	disabled?: boolean
	hideDeclined?: boolean
}

export const VettingSectionStatusSelector: FC<
	VettingSectionStatusSelectorProps
> = ({ value, onChange, disabled, hideDeclined }) => {
	const { t } = useTranslation()

	return (
		<div className='flex gap-3'>
			<Tooltip
				content={t('vetting:accepted')}
				isOpenOnClickEnabled={false}
				rootProps={{
					open: value === 'accepted' || disabled ? false : undefined
				}}
				triggerProps={{ asChild: true }}
			>
				<ButtonLegacy
					disabled={!!disabled}
					onClick={() => value !== 'accepted' && onChange('accepted')}
					type='button'
					variant={
						value === 'accepted' ? 'primarySuccess' : 'secondary'
					}
				>
					<ThumbsUpIcon
						className={value ? undefined : 'text-success-500'}
					/>
					{value === 'accepted' && (
						<span>{t('vetting:accepted')}</span>
					)}
				</ButtonLegacy>
			</Tooltip>
			{!hideDeclined && (
				<Tooltip
					content={t('vetting:rejected')}
					isOpenOnClickEnabled={false}
					rootProps={{
						open:
							value === 'declined' || disabled ? false : undefined
					}}
					triggerProps={{ asChild: true }}
				>
					<ButtonLegacy
						disabled={!!disabled}
						onClick={() =>
							value !== 'declined' && onChange('declined')
						}
						type='button'
						variant={
							value === 'declined' ? 'primaryDanger' : 'secondary'
						}
					>
						<ThumbsDownIcon
							className={value ? undefined : 'text-danger-500'}
						/>
						{value === 'declined' && (
							<span>{t('vetting:rejected')}</span>
						)}
					</ButtonLegacy>
				</Tooltip>
			)}
			<Tooltip
				content={t('vetting:back_to_draft')}
				isOpenOnClickEnabled={false}
				rootProps={{
					open:
						value === 'back_to_draft' || disabled
							? false
							: undefined
				}}
				triggerProps={{ asChild: true }}
			>
				<ButtonLegacy
					disabled={!!disabled}
					onClick={() =>
						value !== 'back_to_draft' && onChange('back_to_draft')
					}
					type='button'
					variant={
						value === 'back_to_draft' ? 'primaryBlue' : 'secondary'
					}
				>
					<HelpCircleIcon
						className={value ? undefined : 'text-blue-500'}
					/>
					{value === 'back_to_draft' && (
						<span>{t('vetting:back_to_draft')}</span>
					)}
				</ButtonLegacy>
			</Tooltip>
		</div>
	)
}
